/**
 * The folder-git mark in front of a card's branch name.
 *
 * ┌────────────────────────────────────┐
 * │ ⑂ feat/relay-handshake   ⌨  #482   │   ← without it, just "feat/relay…"
 * └────────────────────────────────────┘
 *
 * Ported from t3code's `ThreadWorktreeIndicator`. It answers a question the
 * branch name alone cannot: whether this thread has its own checkout or is
 * working directly in the project directory. Two cards can name the same branch
 * and mean entirely different things — one owns a worktree, the other is
 * sharing yours — and that is exactly when you need to know which is which.
 *
 * Diverges from upstream, which renders nothing without a worktree. Its
 * reasoning — an always-present glyph that is sometimes greyed out spends the
 * branch name's space to say nothing — does not carry, because a *different*
 * glyph is not a greyed-out one: `FolderGit2Icon` for a worktree,
 * `GitBranchIcon` otherwise. The distinction survives, and the branch line stops
 * being the only metadata line in the card that starts with bare text.
 *
 * The glyph sits at `text-muted-foreground/40` — deliberately fainter than the
 * branch text it precedes. It is a qualifier on the branch, not a peer of it,
 * and at full strength it competes with the name for the eye.
 */

import { FolderGit2Icon, GitBranchIcon } from "lucide-react";

import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { formatWorktreePathForDisplay } from "../../worktreeCleanup";
import { resolveActivityCardBranchGlyph } from "./activityThreadCard.logic";

export interface ActivityCardWorktreeGlyphProps {
  readonly threadId: string;
  readonly branch: string | null;
  readonly worktreePath: string | null;
}

export function ActivityCardWorktreeGlyph({
  threadId,
  branch,
  worktreePath,
}: ActivityCardWorktreeGlyphProps) {
  const glyph = resolveActivityCardBranchGlyph({
    branch,
    worktreePath,
    displayPath: worktreePath === null ? "" : formatWorktreePathForDisplay(worktreePath),
  });
  if (glyph === null) return null;
  const Icon = glyph.kind === "worktree" ? FolderGit2Icon : GitBranchIcon;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            role="img"
            aria-label={glyph.tooltip}
            data-testid={`thread-${glyph.kind}-${threadId}`}
            className="inline-flex shrink-0 items-center justify-center"
          />
        }
      >
        <Icon className="size-[calc(var(--acard)*0.75)] text-muted-foreground/40" />
      </TooltipTrigger>
      <TooltipPopup side="top">{glyph.tooltip}</TooltipPopup>
    </Tooltip>
  );
}
